import { useMemo, useState } from 'react'
import { useParams, Navigate, useNavigate } from 'react-router-dom'
import { AgentsHeader, AgentsToolbar, AgentCategorySection, AgentsListTable } from '@/components/projects'
import type { AgentsViewMode } from '@/components/projects'
import { getErrorMessage } from '@/api/errors'
import { useAuth, useDebounce, useAgents } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import type { Agent, AgentCategory } from '@/types'
import { AGENT_CATEGORIES, agentCategory } from '@/types'

type SectionState = Record<AgentCategory, boolean>

const EMPTY_TEXT: Record<AgentCategory, { none: string; noMatch: string }> = {
  System: { none: 'No system agents available.', noMatch: 'No system agents match your search.' },
  Custom: { none: 'No custom agents yet. Create one to give your workflows a configured AI worker.', noMatch: 'No custom agents match your search.' },
}

function readSessionViewMode(projectId: string): AgentsViewMode {
  try {
    const stored = sessionStorage.getItem(`agents-view-mode-${projectId}`)
    if (stored === 'list' || stored === 'grid') return stored
    return 'grid'
  } catch {
    return 'grid'
  }
}

function readSessionSections(projectId: string): SectionState {
  const state: SectionState = { System: true, Custom: true }
  try {
    const stored = sessionStorage.getItem(`agents-sections-${projectId}`)
    const parsed: unknown = stored ? JSON.parse(stored) : null
    if (parsed && typeof parsed === 'object') {
      for (const category of AGENT_CATEGORIES) {
        const value = (parsed as Record<string, unknown>)[category]
        if (typeof value === 'boolean') state[category] = value
      }
    }
    return state
  } catch {
    return state
  }
}

function persistSession(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value)
  } catch {
    return
  }
}

function matchesSearch(agent: Agent, term: string): boolean {
  if (!term) return true
  return (
    agent.displayName.toLowerCase().includes(term) ||
    agent.name.toLowerCase().includes(term) ||
    (agent.description?.toLowerCase().includes(term) ?? false) ||
    (agent.latestVersion?.toLowerCase().includes(term) ?? false)
  )
}

function groupByCategory(agents: Agent[]): Record<AgentCategory, Agent[]> {
  const groups: Record<AgentCategory, Agent[]> = { System: [], Custom: [] }
  for (const agent of agents) groups[agentCategory(agent)].push(agent)
  return groups
}

export function AgentsPage() {
  const { projectId } = useParams()
  const { hasPermission, isLoading: authLoading } = useAuth(projectId)

  if (authLoading || !projectId) {
    return null
  }

  if (!hasPermission(Permissions.Project.Agent.Read, projectId)) {
    return <Navigate to={`/projects/${projectId}/backlog`} replace />
  }

  return <AgentsPageContent key={projectId} projectId={projectId} />
}

function AgentsPageContent({ projectId }: { projectId: string }) {
  const { data: agents = [], isLoading, isError, error } = useAgents(projectId)
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const [viewMode, setViewMode] = useState<AgentsViewMode>(() => readSessionViewMode(projectId))
  const [sectionExpanded, setSectionExpanded] = useState<SectionState>(() => readSessionSections(projectId))

  const term = debouncedSearch.trim().toLowerCase()
  const allByCategory = useMemo(() => groupByCategory(agents), [agents])
  const filtered = useMemo(() => agents.filter(agent => matchesSearch(agent, term)), [agents, term])
  const byCategory = useMemo(() => groupByCategory(filtered), [filtered])

  const handleViewModeChange = (mode: AgentsViewMode) => {
    setViewMode(mode)
    persistSession(`agents-view-mode-${projectId}`, mode)
  }

  const handleToggleSection = (category: AgentCategory) => {
    const next = { ...sectionExpanded, [category]: !sectionExpanded[category] }
    setSectionExpanded(next)
    persistSession(`agents-sections-${projectId}`, JSON.stringify(next))
  }

  const noMatches = term.length > 0 && agents.length > 0 && filtered.length === 0

  const emptyTextFor = (category: AgentCategory) =>
    term && allByCategory[category].length > 0 ? EMPTY_TEXT[category].noMatch : EMPTY_TEXT[category].none

  let body
  if (isLoading) {
    body = <p className="py-4 text-sm text-muted-foreground" role="status" aria-live="polite">Loading agents…</p>
  } else if (isError) {
    body = (
      <div className="rounded-2xl border border-dashed border-border bg-table-row p-8 text-center text-sm text-destructive" role="alert">
        {getErrorMessage(error) ?? "Couldn't load agents. Refresh to try again."}
      </div>
    )
  } else if (noMatches) {
    body = (
      <div className="py-12 text-center text-sm text-muted-foreground" role="status" data-testid="agents-no-results">
        No agents match “{debouncedSearch.trim()}”. Try a different name, description or version.
      </div>
    )
  } else {
    body = viewMode === 'list' ? (
      filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-table-row p-8 text-center text-sm text-muted-foreground" data-testid="agents-list-empty">
          No agents yet. Create one to give your workflows a configured AI worker.
        </div>
      ) : (
        <AgentsListTable projectId={projectId} agents={filtered} label="Agents" />
      )
    ) : (
      <div className="space-y-2">
        {AGENT_CATEGORIES.map(category => (
          <AgentCategorySection
            key={category}
            projectId={projectId}
            category={category}
            agents={byCategory[category]}
            isExpanded={sectionExpanded[category]}
            onToggle={() => handleToggleSection(category)}
            emptyText={emptyTextFor(category)}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-6" data-testid="agents-page">
      <AgentsHeader projectId={projectId} onCreateAgent={() => navigate(`/projects/${projectId}/workflows/agents/new/config`)} />
      <AgentsToolbar search={search} onSearchChange={setSearch} viewMode={viewMode} onViewModeChange={handleViewModeChange} />
      {body}
    </div>
  )
}
